import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
export interface AttentionItem {
  id: string;
  /** What the operator must decide or do. Written as an instruction, not a metric. */
  title: string;
  detail: string;
  meta?: string;
  urgency: 'now' | 'soon' | 'watch';
  action?: {
    label: string;
    to: string;
  };
}
const URGENCY: Record<AttentionItem['urgency'], {
  label: string;
  tone: 'danger' | 'warn' | 'neutral';
}> = {
  now: {
    label: 'Needs a decision',
    tone: 'danger'
  },
  soon: {
    label: 'This week',
    tone: 'warn'
  },
  watch: {
    label: 'Worth watching',
    tone: 'neutral'
  }
};

/**
 * The first thing an operator console should answer: what is waiting on me.
 *
 * Ordered by what blocks someone else, not by recency — a person waiting on your
 * decision outranks a number that moved. Items with nothing outstanding are not
 * padded out with metrics.
 */
export function AttentionBoard({
  items,
  summary,
  emptyNote
}: {
  items: AttentionItem[];
  summary: {
    label: string;
    value: string;
    note?: string;
  }[];
  emptyNote: string;
}) {
  const order: AttentionItem['urgency'][] = ['now', 'soon', 'watch'];
  const sorted = [...items].sort((a, b) => order.indexOf(a.urgency) - order.indexOf(b.urgency));
  const blocking = sorted.filter(item => item.urgency === 'now').length;
  return <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-4055" data-mp="25wny53cf2dhw">
      <Panel title={blocking > 0 ? `${blocking} thing${blocking === 1 ? '' : 's'} are waiting on you` : 'Nothing is blocked on you'} description="Ordered by who is waiting, not by what changed most recently." bodyClassName="p-0" data-id="element-4056" data-mp="21vua89yz4yp0">
        {sorted.length === 0 ? <p className="flex items-start gap-2 px-5 py-5 text-body-sm text-ink sm:px-6" data-id="element-4057" data-mp="1n3jy908lv4eu">
            <CheckCircle2Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-4058" data-mp="1i4qw23yrqztz" />
            {emptyNote}
          </p> : <ul className="divide-y divide-line" data-id="element-4059" data-mp="3n7mfo5dz9v6u">
            {sorted.map(item => <li key={item.id} className="px-5 py-4 sm:px-6" data-id="element-4060" data-mp="3rz4jd17dy9vi">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5" data-id="element-4061" data-mp="3rznyyb9kcm6d">
                  <div className="min-w-0" data-id="element-4062" data-mp="rrz2db7hgbd">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-4063" data-mp="2zaqk6i4k97id">
                      <p className="text-body-lg font-semibold text-ink" data-id="element-4064" data-mp="349a98regazey">{item.title}</p>
                      <Badge tone={URGENCY[item.urgency].tone} data-id="element-4065" data-mp="34b4rtz292ffv">{URGENCY[item.urgency].label}</Badge>
                    </div>
                    <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-4066" data-mp="3daht44g7zxpi">{item.detail}</p>
                    {item.meta && <p className="cz-num mt-1 text-body-sm text-ink-muted" data-id="element-4067" data-mp="ohdzt57883lh">{item.meta}</p>}
                  </div>
                  {item.action && <Link to={item.action.to} data-id="element-4068" data-mp="2d2bfvd4924r8">
                      <Button size="sm" variant="secondary" data-id="element-4069" data-mp="2d10cvfimp116">
                        {item.action.label}
                      </Button>
                    </Link>}
                </div>
              </li>)}
          </ul>}
      </Panel>

      <Panel title="Where you stand" bodyClassName="p-0" data-id="element-4070" data-mp="1z9lqcypiihpd">
        <dl className="divide-y divide-line" data-id="element-4071" data-mp="1ub22utv9sxuw">
          {summary.map(row => <div key={row.label} className="px-5 py-3.5 sm:px-6" data-id="element-4072" data-mp="1u8xqdnjb2qj0">
              <dt className="text-body-sm text-ink-muted" data-id="element-4073" data-mp="1pantn8hxnhva">{row.label}</dt>
              <dd className="cz-num mt-0.5 text-h4 font-semibold text-ink" data-id="element-4074" data-mp="1l9u7oko1wd51">{row.value}</dd>
              {row.note && <p className="mt-1 text-caption text-ink-subtle" data-id="element-4075" data-mp="1gckk7ak82bju">{row.note}</p>}
            </div>)}
        </dl>
      </Panel>
    </div>;
}